<template>
  <div id="app">

    <page-header></page-header>

    <page-nav :content-data="ContentData"></page-nav>

    <page-body :content-data="ContentData"></page-body>

    <page-footer></page-footer>

    <icon-spritesheet></icon-spritesheet>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue';
import PageNav from '@/components/PageNav.vue';
import PageBody from '@/components/PageBody.vue';
import PageFooter from '@/components/PageFooter.vue';
import IconSpritesheet from '@/components/IconSpritesheet.vue';

// Import content
import ContentData from '@/assets/json/content.json';

export default {
    components: {
        pageHeader: PageHeader,
        pageNav: PageNav,
        pageBody: PageBody,
        pageFooter: PageFooter,
        iconSpritesheet: IconSpritesheet,
    },
    data() {
        return {
            ContentData,
        };
    },
};
</script>

<style lang="scss">
    // Base
    // ====
    html {
        text-size-adjust: 100%; // Ensure mobile and tablet browsers show text at the correct size
        height: 100%;

        // Sets 1rem = 10px. http://snook.ca/archives/html_and_css/font-size-with-rem
        font-size: 62.5%;

        // Add important variables to css custom properties
        @each $bp, $value in $mq-breakpoints {
            --breakpoint-#{$bp}: #{$value};
        }

        @include mq($from: $site-max-width) {
            margin-left: calc(100vw - 100%);
            margin-right: 0;
        }
    }

    body {
        min-height: 100%;
        display: flex;
        flex-direction: column;
    }

    // Some hard resets
    *,
    *:before,
    *:after {
        padding: 0;
        margin: 0;
        box-sizing: border-box;
    }

    // Layout
    // ======
    #app {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        margin: 0 auto;
        padding: $mobile-gutter;
        width: 100%;
        max-width: $site-max-width;

        // Tablet styles
        @include mq($from: md) {
            padding: $tablet-gutter;
        }

        // Desktop styles
        @include mq($from: ml) {
            padding: $desktop-gutter;
        }
    }

    // Font faces
    // ==========
    // Roboto-Thin - latin
    @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 100;
        font-display: swap;
        src: local('Roboto Thin'), local('Roboto-Thin'),
            url('/assets/fonts/Roboto-Thin.woff2') format('woff2'),
            url('/assets/fonts/Roboto-Thin.woff') format('woff');
    }

    // Roboto-Regular - latin
    @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: local('Roboto'), local('Roboto-Regular'),
            url('/assets/fonts/Roboto-Regular.woff2') format('woff2'),
            url('/assets/fonts/Roboto-Regular.woff') format('woff');
    }

    // Roboto-Black - latin
    @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 900;
        font-display: swap;
        src: local('Roboto Black'), local('Roboto-Black'),
            url('/assets/fonts/Roboto-Black.woff2') format('woff2'),
            url('/assets/fonts/Roboto-Black.woff') format('woff');
    }

    // Typography
    // ==========
    body {
        font-family: 'Roboto', sans-serif;
        font-size: 1.4rem;

        @include mq($from: md) {
            font-size: 1.6rem;
        }
    }

    p {
        margin-bottom: 1.5rem;

        &:last-of-type {
            margin-bottom: 0;
        }
    }

    a {
        color: black;
        text-decoration: underline;
        text-decoration-color: $color-primary;
        transition: color $default-transition-duration $default-transition-style;

        &:hover {
            color: $color-primary;
        }
    }

    ::selection {
        color: white;
        background-color: $color-primary;
    }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0,0,0,0);
        white-space: nowrap;
        border: 0;
    }

    // Visible breakpoints
    // ===================
    // Shows the currently active breakpoint in the top right of the screen,
    // only appearS when running dev build.
    @if($env == 'development') {
        body:before {
            background-color: $color-smoke;
            border-bottom: 1px solid $color-battleship;
            border-left: 1px solid $color-battleship;
            color: $color-slate;
            font: small-caption;
            padding: 3px 6px;
            pointer-events: none;
            position: fixed;
            right: 0;
            top: 0;
            z-index: 100;
            opacity: 0.6;

            // Loop through the breakpoints that should be shown
            @each $name, $width in $mq-breakpoints {
                @include mq($from: $name) {
                    content: "#{$name}, #{$width}";
                }
            }
        }
    }
</style>
